import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/App';
import Layout from '@/components/Layout';
import SubscriptionLimits from '@/components/SubscriptionLimits';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, MessageSquare, Briefcase, Calendar, TrendingUp, Bell } from 'lucide-react';

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState({
    organizations: 0,
    messages: 0,
    opportunities: 0,
    events: 0,
    unreadMessages: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [orgsRes, messagesRes, oppsRes, eventsRes] = await Promise.all([
        api.get('/organizations'),
        api.get('/messages'),
        api.get('/opportunities'),
        api.get('/events')
      ]);

      const unread = messagesRes.data.filter(m => m.to_user_id === user.id && !m.read).length;

      setStats({
        organizations: orgsRes.data.length,
        messages: messagesRes.data.length,
        opportunities: oppsRes.data.length,
        events: eventsRes.data.length,
        unreadMessages: unread
      });

      // Create recent activity feed
      const activity = [
        ...oppsRes.data.slice(0, 3).map(opp => ({
          type: 'opportunity',
          title: opp.title,
          org: opp.org_name,
          time: new Date(opp.created_at).toLocaleDateString()
        })),
        ...eventsRes.data.slice(0, 3).map(event => ({
          type: 'event',
          title: event.title,
          org: event.org_name,
          time: event.date
        }))
      ].slice(0, 5);

      setRecentActivity(activity);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white shadow-lg">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {user.contact_person}!</h1>
          <p className="text-green-50 text-lg">{user.org_name}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link to="/organizations">
            <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 border-l-green-500" data-testid="stat-card-organizations">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Organizations</CardTitle>
                <Users className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.organizations}</div>
                <p className="text-xs text-gray-500 mt-1">Active connections</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/messages">
            <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 border-l-emerald-500" data-testid="stat-card-messages">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Messages</CardTitle>
                <MessageSquare className="h-5 w-5 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.messages}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.unreadMessages > 0 ? `${stats.unreadMessages} unread` : 'All caught up!'}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/opportunities">
            <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 border-l-teal-500" data-testid="stat-card-opportunities">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Opportunities</CardTitle>
                <Briefcase className="h-5 w-5 text-teal-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.opportunities}</div>
                <p className="text-xs text-gray-500 mt-1">Available now</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/events">
            <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 border-l-lime-500" data-testid="stat-card-events">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Events</CardTitle>
                <Calendar className="h-5 w-5 text-lime-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.events}</div>
                <p className="text-xs text-gray-500 mt-1">Upcoming events</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Subscription Limits */}
        <SubscriptionLimits />

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Recent Activity
              </CardTitle>
              <CardDescription>Latest opportunities and events in the network</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-gray-500">Loading...</p>
              ) : recentActivity.length > 0 ? (
                <div className="space-y-4">
                  {recentActivity.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        item.type === 'opportunity' ? 'bg-teal-100' : 'bg-lime-100'
                      }`}>
                        {item.type === 'opportunity' ? (
                          <Briefcase className="h-5 w-5 text-teal-600" />
                        ) : (
                          <Calendar className="h-5 w-5 text-lime-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                        <p className="text-xs text-gray-500">{item.org}</p>
                        <p className="text-xs text-gray-400 mt-1">{item.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No recent activity</p>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Get started with common tasks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/opportunities">
                <Button className="w-full justify-start bg-teal-600 hover:bg-teal-700 text-white" data-testid="quick-action-post-opportunity">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Post an Opportunity
                </Button>
              </Link>
              <Link to="/events">
                <Button className="w-full justify-start bg-lime-600 hover:bg-lime-700 text-white" data-testid="quick-action-create-event">
                  <Calendar className="h-4 w-4 mr-2" />
                  Create an Event
                </Button>
              </Link>
              <Link to="/organizations">
                <Button className="w-full justify-start bg-green-600 hover:bg-green-700 text-white" data-testid="quick-action-browse-organizations">
                  <Users className="h-4 w-4 mr-2" />
                  Browse Organizations
                </Button>
              </Link>
              <Link to="/messages">
                <Button className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quick-action-send-message">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send a Message
                </Button>
              </Link>
              <div className="pt-4 border-t">
                <Link to="/pricing">
                  <Button variant="outline" className="w-full justify-start border-green-600 text-green-600 hover:bg-green-50" data-testid="quick-action-subscribe">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Subscribe to CEC Connect
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;